import { useState, useContext, useRef, useEffect, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { TextField, Button, Box, Typography, Alert, Divider } from '@mui/material';
import AuthContext from '../contexts/AuthContext';
import { useAxios } from '../hooks/useAxios';
import { getMicrosoftSsoErrorMessage } from '../utils/microsoftSsoErrors';
import { resolveApiUrl } from '../utils/resolveApiUrl';

const MicrosoftLogoIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    viewBox="0 0 21 21"
    aria-hidden="true"
  >
    <rect x="1" y="1" width="9" height="9" fill="#f25022" />
    <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
    <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
  </svg>
);

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [microsoftSsoEnabled, setMicrosoftSsoEnabled] = useState(false);
  const emailRef = useRef(null);
  const passwordRef = useRef(null);
  const { login } = useContext(AuthContext);
  const { sendRequest, loadingStatus } = useAxios();
  const history = useHistory();
  const location = useLocation();

  // Android/Chrome autofill often fills the DOM without firing React onChange
  const syncAutofilledValues = useCallback(() => {
    const nextEmail = emailRef.current?.value ?? '';
    const nextPassword = passwordRef.current?.value ?? '';
    if (nextEmail) {
      setEmail((prev) => (prev === nextEmail ? prev : nextEmail));
    }
    if (nextPassword) {
      setPassword((prev) => (prev === nextPassword ? prev : nextPassword));
    }
  }, []);

  useEffect(() => {
    const timeouts = [50, 200, 500, 1000].map((ms) => setTimeout(syncAutofilledValues, ms));
    return () => timeouts.forEach(clearTimeout);
  }, [syncAutofilledValues]);

  useEffect(() => {
    let cancelled = false;
    const apiBase = (resolveApiUrl() || '').replace(/\/$/, '');
    (async () => {
      try {
        const response = await fetch(`${apiBase}/users/microsoft/status`, {
          credentials: 'include',
        });
        if (!response.ok) {
          throw new Error('SSO status unavailable');
        }
        const data = await response.json();
        if (!cancelled) {
          setMicrosoftSsoEnabled(Boolean(data?.enabled));
        }
      } catch (_error) {
        if (!cancelled) {
          setMicrosoftSsoEnabled(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ssoError = params.get('ssoError');
    if (!ssoError) {
      return;
    }
    setError(getMicrosoftSsoErrorMessage(ssoError));
    history.replace('/login');
  }, [location.search, history]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Prefer live DOM values in case autofill never synced into state
    const emailValue = (emailRef.current?.value || email).trim();
    const passwordValue = passwordRef.current?.value || password;

    try {
      const response = await sendRequest(
        '/users/login',
        'POST',
        { email: emailValue, password: passwordValue },
        {},
        'Přihlašuji . . .'
      );

      if (response.employeeId) {
        if (document.activeElement && document.activeElement.blur) {
          document.activeElement.blur();
        }
        login(
          undefined,
          response.employeeId,
          response.userName || response.userSettings?.fullName || emailValue,
          response.expirationDate,
          response.userSettings || null,
          response.featurePermissions || [],
          response.masterDataPermissions || null,
          response.isAdmin || false
        );
        history.push('/my-shifts');
      } else {
        setError(getMicrosoftSsoErrorMessage('employee_required'));
      }
    } catch (err) {
      setError('Chyba při přihlášení. Zkontrolujte své údaje a zkuste to znovu.');
    }
  };

  const microsoftLoginHandler = () => {
    // Drop expired mirror so post-SSO restoreSession trusts the new cookie on first load.
    localStorage.removeItem('compassMobileAuth');
    const apiBase = (resolveApiUrl() || '').replace(/\/$/, '');
    const frontendOrigin = encodeURIComponent(window.location.origin);
    window.location.href = `${apiBase}/users/microsoft/start?client=mobile&frontendOrigin=${frontendOrigin}`;
  };

  return (
    <div className="glass-auth-shell">
      <Box
        className="glass-panel glass-auth-card"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <Typography component="h1" variant="h4" className="accent-text" sx={{ mb: 3 }}>
          Směny Compass
        </Typography>

        {error && (
          <Alert severity="error" className="glass-alert" sx={{ width: '100%', mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
          <TextField
            inputRef={emailRef}
            margin="normal"
            required
            fullWidth
            id="email"
            label="Email"
            name="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            InputLabelProps={{ shrink: Boolean(email) || undefined }}
            inputProps={{
              autoCorrect: 'off',
              spellCheck: 'false',
              autoCapitalize: 'none',
              onInput: syncAutofilledValues,
            }}
          />
          <TextField
            inputRef={passwordRef}
            margin="normal"
            required
            fullWidth
            name="password"
            label="Heslo"
            type="password"
            id="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            InputLabelProps={{ shrink: Boolean(password) || undefined }}
            inputProps={{
              onInput: syncAutofilledValues,
            }}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            className="glass-accent-fill"
            sx={{ mt: 3, mb: 2, py: 1.5 }}
            disabled={loadingStatus.isLoading}
          >
            {loadingStatus.isLoading ? 'Přihlašuji...' : 'Přihlásit se'}
          </Button>
        </Box>
        {microsoftSsoEnabled && (
          <>
            <Divider sx={{ width: '100%', my: 1 }}>nebo</Divider>
            <Button
              type="button"
              fullWidth
              variant="outlined"
              startIcon={<MicrosoftLogoIcon />}
              sx={{ mt: 1, mb: 1, py: 1.5 }}
              onClick={microsoftLoginHandler}
              disabled={loadingStatus.isLoading}
            >
              Přihlásit firemním Microsoft účtem
            </Button>
          </>
        )}
      </Box>
    </div>
  );
};

export default Login;
